import type { EnvironmentProviders, Provider, Type } from '@angular/core';
import { computed, inject, Injector } from '@angular/core';
import { TokenStore } from '@core/services/token.store';
import { environment } from '@env';
import { ImpersonationSessionStore } from '@features/authentication/stores/impersonation-session.store';
import { AuthorizationFacade } from '@features/authorization/facades/authorization.facade';
import { AuthorizationStore } from '@features/authorization/stores/authorization.store';
import { TRANSLOCO_MISSING_HANDLER } from '@jsverse/transloco';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import type { DxGrantEntry, DxGrantsContext, DxKnownRole } from '@kerberos-compliance/compliance-shared-ui/dx';
import {
  DxI18nService,
  isDxLevelReachableInBuild,
  isDxSuiteReachableInBuild,
  provideDxTooling,
} from '@kerberos-compliance/compliance-shared-ui/dx';
import { DxMissingTranslationHandler } from './dx-missing-translation.handler';
import { dxStorageRedactionPolicies, dxStorageResetGroups } from './dx-redaction';
import type { DxToolId } from './dx-registry';
import { dxToolRegistry } from './dx-registry';
import { dxMissingTranslationSink } from './dx-sinks';
import { dxBuildStatusContributor, dxSessionStatusContributor } from './dx-status.contributors';
import { provideUiDevModePersistence } from './dx-ui-dev-mode.persistence';

const dxKnownRoles: readonly DxKnownRole[] = [
  { role: NamedRoleMap.InternalUser, description: 'Kerberos internal platform operator' },
  { role: NamedRoleMap.SystemAdministrator, description: 'Superadmin privileges across organizations' },
  { role: NamedRoleMap.CustomerImpersonator, description: 'Allows impersonation of customer user accounts' },
  { role: NamedRoleMap.BasicUser, description: 'Standard organization member' },
  { role: NamedRoleMap.WorkItemUser, description: 'Permitted to process KYC & AML work items' },
  { role: NamedRoleMap.BillingUser, description: 'Access to billing and subscriptions' },
];

/**
 * Resolves the session's grants for the shared grants panel.
 *
 * Read straight off `AuthorizationStore`, which is what the app itself gates on. The DX
 * service this replaced re-derived a role checklist and decoded the whole JWT into a
 * copyable blob; both jobs now live in the library, which allowlists the claims.
 *
 * @returns {DxGrantsContext} The grants context for this session.
 */
function dxGrantsContext(): DxGrantsContext {
  const authorizationStore = inject(AuthorizationStore);
  const authorizationFacade = inject(AuthorizationFacade);
  const tokenStore = inject(TokenStore);

  return {
    isLoaded: authorizationStore.grantsLoaded,
    isAuthenticated: tokenStore.isAuthenticated,
    // `?? undefined`: the store models "no token" as `null`, the DX contract as
    // `undefined`, and the panel keys "is there a token at all" off exactly that.
    accessToken: computed(() => tokenStore.accessToken() ?? undefined),
    knownRoles: dxKnownRoles,
    grants: computed<readonly DxGrantEntry[]>(() =>
      authorizationStore.userGrants().map((grant) => ({
        role: grant.roleDisplayName,
        roleId: grant.roleId,
        tenantId: grant.tenantId,
        organizationId: grant.organizationId,
        isPlatformLevel: grant.organizationId === undefined,
      })),
    ),
    reload: () => authorizationFacade.loadGrants(),
  };
}

/**
 * Whether a tool could ever be reached in this build, ignoring session state.
 *
 * Consulted before wiring anything onto the app's own request or translation path, so a
 * build that can never render a tool does not ship its collector either.
 *
 * @param {DxToolId} id - The tool to check.
 * @returns {boolean} True when some session in this build could reach it.
 */
function isToolReachableInBuild(id: DxToolId): boolean {
  return isDxLevelReachableInBuild(dxToolRegistry[id].level, environment.target);
}

/**
 * Wires the shared DX suite for Admin Desk.
 *
 * Every panel is now the shared one: status, grants and storage all come from
 * `compliance-shared-ui/dx`, and this file is the whole of what Admin Desk contributes -
 * its registry, its status sections, its grants context and its storage redaction
 * policies. Nothing here renders anything, which is the point: an app-local copy of the
 * grants panel is how Admin Desk came to disclose an entire decoded JWT while AML Desk's
 * copy allowlisted eight claims.
 *
 * Returns nothing at all where no tool could be reached in this build, so a session that
 * can open nothing does not carry the wiring for it.
 *
 * @returns {(Provider | EnvironmentProviders)[]} Providers to append to the application provider list.
 */
export function dxProviders(): (Provider | EnvironmentProviders)[] {
  if (!isDxSuiteReachableInBuild(dxToolRegistry, environment.target)) {
    return [];
  }

  const providers: (Provider | EnvironmentProviders)[] = provideDxTooling({
    registry: dxToolRegistry,
    target: environment.target,
    badge: 'Admin Desk',
    session: () => {
      const authorizationStore = inject(AuthorizationStore);
      const impersonationStore = inject(ImpersonationSessionStore);

      return {
        hasInternalRole: computed(
          () =>
            authorizationStore.hasRole(NamedRoleMap.InternalUser) ||
            authorizationStore.hasRole(NamedRoleMap.SystemAdministrator),
        ),
        isImpersonating: impersonationStore.hasActiveSession,
      };
    },
    statusContributors: [dxBuildStatusContributor, dxSessionStatusContributor],
    grants: dxGrantsContext,
    storage: {
      policies: dxStorageRedactionPolicies,
      resetGroups: dxStorageResetGroups,
    },
    i18n: () => inject(DxI18nService),
    panels: [
      {
        id: 'status',
        label: '📱 Status',
        tools: ['app-status'],
        panels: [
          {
            tool: 'app-status',
            load: async (): Promise<Type<unknown>> => {
              const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
              return module.DxStatusPanelComponent;
            },
          },
        ],
      },
      {
        id: 'grants',
        label: '🛡️ Grants',
        tools: ['grants-inspector'],
        panels: [
          {
            tool: 'grants-inspector',
            load: async (): Promise<Type<unknown>> => {
              const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
              return module.DxGrantsPanelComponent;
            },
          },
        ],
      },
      {
        id: 'storage',
        label: '💾 Storage',
        tools: ['local-storage'],
        panels: [
          {
            tool: 'local-storage',
            load: async (): Promise<Type<unknown>> => {
              const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
              return module.DxStoragePanelComponent;
            },
          },
        ],
      },
      {
        id: 'a11y',
        label: '♿ a11y & i18n',
        tools: ['a11y-i18n-inspector'],
        panels: [
          {
            tool: 'a11y-i18n-inspector',
            load: async (): Promise<Type<unknown>> => {
              const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
              return module.DxA11yPanelComponent;
            },
          },
        ],
      },
      {
        id: 'ui-dev-mode',
        label: '🧪 UI Dev Mode',
        tools: ['ui-dev-mode'],
        panels: [
          {
            tool: 'ui-dev-mode',
            load: async (): Promise<Type<unknown>> => {
              const module = await import('./dx-ui-dev-mode-panel/dx-ui-dev-mode-panel.component');
              return module.DxUiDevModePanelComponent;
            },
          },
        ],
      },
      {
        id: 'system',
        label: '⚙️ Sentry',
        tools: ['sentry-trigger'],
        panels: [
          {
            tool: 'sentry-trigger',
            load: async (): Promise<Type<unknown>> => {
              const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
              return module.DxSentryPanelComponent;
            },
          },
        ],
      },
    ],
  });

  if (isToolReachableInBuild('a11y-i18n-inspector')) {
    providers.push(
      {
        provide: dxMissingTranslationSink,
        useFactory: (): ((key: string) => void) => {
          const injector = inject(Injector);
          return (key) => injector.get(DxI18nService).logMissingTranslation(key);
        },
      },
      { provide: TRANSLOCO_MISSING_HANDLER, useClass: DxMissingTranslationHandler },
    );
  }

  if (isToolReachableInBuild('ui-dev-mode')) {
    providers.push(provideUiDevModePersistence());
  }

  return providers;
}
